/** Full-page animated dark space background + parallax stars (menu + behind game). */

interface Star {
  x: number;
  y: number;
  r: number;
  a: number;
  /** Parallax layer 0 = far (slow), 1 = mid, 2 = near (fast). */
  layer: 0 | 1 | 2;
}

interface BgStop {
  c0: [number, number, number];
  c1: [number, number, number];
  c2: [number, number, number];
  started: number;
  duration: number;
}

/** Horizontal drift in viewport-widths per second (left → right). */
const LAYER_SPEED = [0.008, 0.022, 0.055] as const;

function darkColor(): [number, number, number] {
  return [4 + Math.random() * 28, 6 + Math.random() * 32, 12 + Math.random() * 40];
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpRgb(a: [number, number, number], b: [number, number, number], t: number): string {
  return `rgb(${Math.round(lerp(a[0], b[0], t))},${Math.round(lerp(a[1], b[1], t))},${Math.round(lerp(a[2], b[2], t))})`;
}

export class SpaceBackground {
  private stars: Star[] = [];
  private bgFrom: BgStop;
  private bgTo: BgStop;
  private bgCache: CanvasGradient | null = null;
  private bgSizeKey = '';
  private bgLastUpdate = 0;
  private running = false;
  private lastTs = 0;
  /** When false, stars stay fixed. Scale multiplies LAYER_SPEED (1 = menu, 0.1 = in-game). */
  private parallaxScale = 1;

  constructor(private canvas: HTMLCanvasElement) {
    const now = performance.now();
    this.bgFrom = {
      c0: darkColor(),
      c1: darkColor(),
      c2: darkColor(),
      started: now,
      duration: 12000 + Math.random() * 8000,
    };
    this.bgTo = {
      c0: darkColor(),
      c1: darkColor(),
      c2: darkColor(),
      started: now,
      duration: this.bgFrom.duration,
    };
    this.regenStars();
  }

  private regenStars(): void {
    const layers: Array<{ layer: 0 | 1 | 2; count: number; rMin: number; rMax: number; aMin: number; aMax: number }> = [
      { layer: 0, count: 120, rMin: 0.35, rMax: 0.9, aMin: 0.2, aMax: 0.45 },
      { layer: 1, count: 70, rMin: 0.5, rMax: 1.35, aMin: 0.35, aMax: 0.65 },
      { layer: 2, count: 35, rMin: 0.8, rMax: 2.0, aMin: 0.5, aMax: 0.9 },
    ];
    this.stars = [];
    for (const L of layers) {
      for (let i = 0; i < L.count; i++) {
        this.stars.push({
          x: Math.random(),
          y: Math.random(),
          r: L.rMin + Math.random() * (L.rMax - L.rMin),
          a: L.aMin + Math.random() * (L.aMax - L.aMin),
          layer: L.layer,
        });
      }
    }
  }

  private resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const w = Math.max(1, Math.floor(window.innerWidth * dpr));
    const h = Math.max(1, Math.floor(window.innerHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.bgCache = null;
    }
  }

  private drawFrame(now: number): void {
    this.resize();
    const ctx = this.canvas.getContext('2d')!;
    const w = this.canvas.width;
    const h = this.canvas.height;

    const dt = this.lastTs ? Math.min(0.05, (now - this.lastTs) / 1000) : 0;
    this.lastTs = now;

    let t = (now - this.bgFrom.started) / this.bgFrom.duration;
    if (t >= 1) {
      this.bgFrom = { ...this.bgTo, started: now, duration: 14000 + Math.random() * 10000 };
      this.bgTo = {
        c0: darkColor(),
        c1: darkColor(),
        c2: darkColor(),
        started: now,
        duration: this.bgFrom.duration,
      };
      t = 0;
      this.bgCache = null;
    }

    const sizeKey = `${w}x${h}`;
    if (!this.bgCache || sizeKey !== this.bgSizeKey || now - this.bgLastUpdate > 100) {
      const ease = t * t * (3 - 2 * t);
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, lerpRgb(this.bgFrom.c0, this.bgTo.c0, ease));
      g.addColorStop(0.5, lerpRgb(this.bgFrom.c1, this.bgTo.c1, ease));
      g.addColorStop(1, lerpRgb(this.bgFrom.c2, this.bgTo.c2, ease));
      this.bgCache = g;
      this.bgSizeKey = sizeKey;
      this.bgLastUpdate = now;
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = this.bgCache;
    ctx.fillRect(0, 0, w, h);

    for (const s of this.stars) {
      if (this.parallaxScale > 0 && dt > 0) {
        s.x += LAYER_SPEED[s.layer] * this.parallaxScale * dt;
        if (s.x > 1) s.x -= 1;
      }
      ctx.fillStyle = `rgba(230,235,255,${s.a})`;
      ctx.beginPath();
      ctx.arc(s.x * w, s.y * h, s.r * (0.85 + s.layer * 0.15), 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /** Parallax speed scale: 1 = menu, 0.1 = in-game (10× slower), 0 = frozen. */
  setParallax(scale: number): void {
    this.parallaxScale = Math.max(0, scale);
    if (scale > 0) this.lastTs = 0;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const loop = (ts: number) => {
      if (!this.running) return;
      this.drawFrame(ts);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }
}
